/**
 * TaxReport.jsx — R3
 * Route: /pos/reports/tax-report
 * API:   GET /api/pos/reports/tax-report
 *
 * Tax collected per tax type across a date range.
 * Two sub-tabs: Detail (date-wise) and Summary (collapsed totals).
 * Complementary orders excluded by default — toggle via filter.
 */

import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Popover, Spin, Tabs, Tag } from "antd";
import { Download, Filter, Calendar } from "react-feather";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import SelectComponent from "react-select";
import PrintReportPreview from "../../reports/PrintReportPreview";

const getToken    = () => localStorage.getItem("token");
const getApiUrl = () => {
  const base = (process.env.REACT_APP_API_URL || "http://localhost:3000/api").replace(/\/+$/, "");
  return base //.endsWith("/pos") ? base : `${base}/pos`;
};
const getCurrency = () => localStorage.getItem("currencycode") || "₹";
const BUSINESS_DATE_KEYS = ["posbusiness_date", "business_date", "pos_business_date"];
const getBizDate = () => {
  for (const key of BUSINESS_DATE_KEYS) {
    const d = localStorage.getItem(key);
    if (d && moment(d).isValid()) return moment(d).startOf("day");
  }
  return getBizDate().clone().startOf("day");
};
const pillStyle   = (bg, color) => ({ background: bg, color, fontWeight: 800, fontSize: 12, padding: "5px 10px", borderRadius: 999, border: `1px solid ${color}33`, whiteSpace: "nowrap" });
const downloadCSV = (columns, data, filename) => {
  const h = columns.filter(c=>c.dataIndex).map(c=>c.title).join(",");
  const r = data.map(row => columns.filter(c=>c.dataIndex).map(c=>`"${String(row[c.dataIndex]??"").replace(/"/g,'""')}"`).join(","));
  const blob = new Blob([[h,...r].join("\n")],{type:"text/csv;charset=utf-8;"}); const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href=url; a.download=filename; a.click(); URL.revokeObjectURL(url);
};

const TaxReport = () => {
  const currency = getCurrency(); const apiURL = getApiUrl();
  const [dateRange,     setDateRange]     = useState({ startDate: getBizDate(), endDate: getBizDate() });
  const [activeTab,     setActiveTab]     = useState("detail");
  const [loading,       setLoading]       = useState(false);
  const [detail,        setDetail]        = useState([]);
  const [summary,       setSummary]       = useState([]);
  const [filterVisible, setFilterVisible] = useState(false);
  const [previewVisible,setPreviewVisible]= useState(false);
  const [exportData,    setExportData]    = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [filters,       setFilters]       = useState({ order_type: null, is_complementary: { value: "false", label: "No" }});

  const orderTypeOptions = ["Table Service","Room Service","Delivery","Takeaway"].map(v=>({value:v,label:v}));
  const boolOptions = [{value:"true",label:"Include Comps"},{value:"false",label:"Exclude Comps"}];

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start_date: dateRange.startDate.format("YYYY-MM-DD"),
        end_date:   dateRange.endDate.format("YYYY-MM-DD"),
        ...(filters.order_type       && { order_type:       filters.order_type.value }),
        ...(filters.is_complementary && { is_complementary: filters.is_complementary.value }),
      };
      const res = await axios.get(`${apiURL}/pos/tax-report`, { headers:{ Authorization:`Bearer ${getToken()}` }, params });
      if (res.data.success) { setDetail(res.data.detail||[]); setSummary(res.data.summary||[]); }
    } catch(err) { Swal.fire({icon:"error",title:"Error",text:err?.response?.data?.error||err.message}); }
    finally { setLoading(false); }
  }, [dateRange, filters, apiURL]);

  useEffect(()=>{ fetchReport(); },[fetchReport]);

  const detailColumns = [
    { title: "Date",         dataIndex: "business_date",    width: 120 },
    { title: "Tax Name",     dataIndex: "tax_name",         width: 120 },
    { title: "Rate (%)",     dataIndex: "tax_rate",         width: 90,  align:"right" },
    { title: "Bills",        dataIndex: "bill_count",       width: 80,  align:"right" },
    { title: `Taxable Subtotal (${currency})`, dataIndex:"taxable_subtotal", width:160, align:"right", render:v=>Number(v||0).toFixed(2) },
    { title: `Tax Collected (${currency})`,    dataIndex:"tax_collected",    width:150, align:"right", fixed:"right",
      render:v=><strong>{Number(v||0).toFixed(2)}</strong> },
  ];
  const summaryColumns = [
    { title: "Tax Name",  dataIndex: "tax_name",          width: 140 },
    { title: "Rate (%)", dataIndex: "tax_rate",           width: 100, align:"right" },
    { title: "Bills",    dataIndex: "bill_count",         width: 90,  align:"right" },
    { title: `Taxable Subtotal (${currency})`, dataIndex:"taxable_subtotal", width:170, align:"right", render:v=>Number(v||0).toFixed(2) },
    { title: `Total Tax Collected (${currency})`, dataIndex:"total_tax_collected", width:180, align:"right", fixed:"right",
      render:v=><strong style={{color:"#166534"}}>{Number(v||0).toFixed(2)}</strong> },
  ];

  const currentCols = activeTab==="detail" ? detailColumns : summaryColumns;
  const currentData = activeTab==="detail" ? detail : summary;
  const grandTax    = summary.reduce((s,r)=>s+Number(r.total_tax_collected||0),0);

  return (
    <div className="page-wrapper"><div className="content container-fluid">
      <div className="row align-items-center mb-3">
        <div className="col-auto d-flex align-items-center gap-3">
          <h2 style={{fontSize:20,fontWeight:700,borderBottom:"2px solid #007bff",paddingBottom:4,margin:0}}>Tax Collection Report</h2>
          <DateRangePicker
            key={`${dateRange.startDate.format("YYYY-MM-DD")}-${dateRange.endDate.format("YYYY-MM-DD")}`}
            initialSettings={{ startDate:dateRange.startDate.toDate(), endDate:dateRange.endDate.toDate(),
              ranges:{ Today:[getBizDate().clone().startOf("day").toDate(),getBizDate().clone().endOf("day").toDate()],
                "This Month":[getBizDate().clone().startOf("month").toDate(),getBizDate().clone().endOf("month").toDate()],
                "Last Month":[getBizDate().clone().subtract(1,"month").startOf("month").toDate(),getBizDate().clone().subtract(1,"month").endOf("month").toDate()] },
              locale:{format:"DD MMM YYYY",applyLabel:"Apply",cancelLabel:"Cancel"}, alwaysShowCalendars:true, autoApply:false }}
            onApply={(_,p)=>setDateRange({startDate:p.startDate,endDate:p.endDate})}>
            <div className="input-group" style={{width:250}}>
              <input type="text" className="form-control text-center fw-bold"
                style={{height:38,fontSize:14,borderRight:"none",background:"#fff"}}
                value={`${dateRange.startDate.format("DD MMM YYYY")} – ${dateRange.endDate.format("DD MMM YYYY")}`} readOnly />
              <span className="input-group-text" style={{background:"#fff",borderLeft:"none",height:38,cursor:"pointer"}}>
                <Calendar size={15} color="#495057" />
              </span>
            </div>
          </DateRangePicker>
        </div>
        <div className="col-auto ms-auto d-flex align-items-center gap-2">
          {grandTax > 0 && <span style={pillStyle("#fef9c3","#854d0e")}>{currency} {grandTax.toFixed(2)} Total Tax</span>}
          <Popover open={filterVisible} onOpenChange={setFilterVisible} placement="bottomRight" trigger="click"
            title="Filters"
            content={<div style={{width:240,display:"grid",gap:12}}>
              {[{label:"Order Type",key:"order_type",options:orderTypeOptions},{label:"Complementary",key:"is_complementary",options:boolOptions}].map(f=>(
                <div key={f.key}>
                  <label style={{fontSize:12,fontWeight:700,display:"block",marginBottom:4}}>{f.label}</label>
                  <SelectComponent isClearable options={f.options} value={filters[f.key]}
                    onChange={v=>setFilters(p=>({...p,[f.key]:v}))} styles={{control:b=>({...b,fontSize:13})}} />
                </div>))}
              <Button type="primary" size="small" block onClick={()=>{setFilterVisible(false);fetchReport();}}>Apply</Button>
            </div>}>
            <Button type="default" className="d-flex align-items-center" style={{height:38}}>
              <Filter size={15} className="me-2" /> Filter
            </Button>
          </Popover>
          <div className="dropdown">
            <Button type="primary" className="d-flex align-items-center dropdown-toggle" data-bs-toggle="dropdown" style={{height:38}}>
              <Download size={15} className="me-2" /> Download
            </Button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li><button className="dropdown-item" onClick={()=>downloadCSV(currentCols,currentData,`Tax_Report_${activeTab}_${dateRange.startDate.format("YYYYMMDD")}.csv`)}>Download CSV</button></li>
              <li><button className="dropdown-item" onClick={()=>{setExportData(currentData);setExportHeaders(currentCols.filter(c=>c.dataIndex).map(c=>({label:c.title,key:c.dataIndex})));setPreviewVisible(true);}}>Print / PDF Preview</button></li>
            </ul>
          </div>
        </div>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{marginBottom:12}}
        items={[{key:"detail",label:"Date-wise Detail"},{key:"summary",label:"Collapsed Summary"}]} />

      {loading ? <div className="text-center my-5"><Spin size="large" /></div> : (
        <Table columns={currentCols} dataSource={currentData} rowKey={(r,i)=>`${r.business_date||""}_${r.tax_name||""}_${i}`}
          scroll={{x:"max-content"}} size="small" bordered
          pagination={{pageSize:100,showTotal:t=>`${t} records`}} />
      )}
      <PrintReportPreview visible={previewVisible} onClose={()=>setPreviewVisible(false)}
        reportTitle={`Tax Collection Report — ${dateRange.startDate.format("DD MMM YYYY")} to ${dateRange.endDate.format("DD MMM YYYY")}`}
        propertyName="" data={exportData} headers={exportHeaders}
        footerData="" totalColumns={[]} apiEndpoint="transactions/generate-pdf" apiUrl={apiURL} />
    </div></div>
  );
};

export default TaxReport;
